<template>
  <div>
    <dx-description-label v-if="$te(`${translationKeyPrefix}.description`)">
      {{ $t(`${translationKeyPrefix}.description`) }}
    </dx-description-label>
    <div
      v-for="(location, i) in internalValue"
      :key="`${location}_${i}`"
      :disabled="config.readonly"
      class="locationForm"
    >
      <div class="locationFormHeader">
        <dx-description-label>Standort {{ i + 1 }}</dx-description-label>
        <button
          class="deleteButton"
          :title="$t('remove')"
          @click="removeLocation(i)"
        >
          <trash-icon :width="22" :height="22" />
        </button>
      </div>

      <dx-description-label v-if="config.infoText">
        {{ config.infoText }}
      </dx-description-label>

      <dx-input
        :id="`${config.id}-${i}-street`"
        v-model="location.street"
        :disabled="config.readonly"
        :label="$t(`${translationKeyPrefix}.street.label`)"
        :error="getErrorMessage(error, 'street', i)"
        @blur="onBlur('street', i)"
      />

      <dx-input
        :id="`${config.id}-${i}-houseNumber`"
        v-model="location.houseNumber"
        :disabled="config.readonly"
        :label="$t(`${translationKeyPrefix}.houseNumber.label`)"
        :error="getErrorMessage(error, 'houseNumber', i)"
        @blur="onBlur('houseNumber', i)"
      />

      <dx-input
        :id="`${config.id}-${i}-zip`"
        v-model="location.zip"
        :disabled="config.readonly"
        :label="$t(`${translationKeyPrefix}.zip.label`)"
        :error="getErrorMessage(error, 'zip', i)"
        @blur="onBlur('zip', i)"
      />

      <dx-input
        :id="`${config.id}-${i}-city`"
        v-model="location.city"
        :disabled="config.readonly"
        :label="$t(`${translationKeyPrefix}.city.label`)"
        :error="getErrorMessage(error, 'city', i)"
        @blur="onBlur('city', i)"
      />

      <dx-select
        :id="`${config.id}-${i}-country`"
        v-model="location.country"
        :label="$t(`${translationKeyPrefix}.country.label`)"
        :options="countries"
        :error="getErrorMessage(error, 'country', i)"
        class="selection"
      />
    </div>

    <button type="button" class="addLocation" @click="addLocation">
      <map-pin-icon />
      <span>{{ $t(`${translationKeyPrefix}.addLocation`) }}</span>
    </button>
  </div>
</template>

<script>
import { DxInput, DxSelect } from '@sumcumo/dextra-frontend-component-lib'

import DxDescriptionLabel from '~/components/DX-DescriptionLabel/DxDescriptionLabel'
import MapPinIcon from '~/components/icons/MapPinIcon'
import TrashIcon from '~/components/icons/TrashIcon'

import countrylist from './countries.json'

export default {
  name: 'DxLocationsFieldAdapter',
  components: {
    MapPinIcon,
    DxDescriptionLabel,
    DxInput,
    DxSelect,
    TrashIcon,
  },

  props: {
    config: {
      type: Object,
      required: true,
    },
    value: {
      type: Array,
      required: true,
    },
    error: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      internalValue: this.value,
    }
  },

  computed: {
    translationKeyPrefix() {
      if (this.config.source) {
        if (this.config.source.kind === 'values') {
          return `attributes.${this.config.source.key}.${this.config.source.originalId}`
        }

        return `${this.config.source.key}.${this.config.source.originalId}`
      }

      return ''
    },

    countries() {
      const values = Object.keys(countrylist[this.$i18n.locale]).map(
        (value) => ({
          value,
          label: countrylist[this.$i18n.locale][value],
        }),
      )

      return [
        {
          label: this.$t('selectOption'),
          caption: true,
        },
        ...values,
      ]
    },
  },

  watch: {
    internalValue() {
      this.$emit('input', this.internalValue)
    },
  },

  methods: {
    getErrorMessage(errors, propKey, index) {
      const error = errors?.[index]?.[propKey]
      if (!error) {
        return null
      }

      const key = `errors.locations.${propKey}.${error.type}`
      return this.$t(key, this.error.params)
    },

    addLocation() {
      this.internalValue.push({
        zip: '',
        city: '',
        country: '',
        houseNumber: '',
        street: '',
      })
    },

    removeLocation(index) {
      this.internalValue.splice(index, 1)
    },

    onBlur(field, index) {
      this.emit('validate', `${this.config.id}[${index}].${field}`)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/variables/colors';
@import '~@sumcumo/dextra-frontend-component-lib/dist/assets/styles/variables/typo';

.locationForm {
  display: grid;
  margin-bottom: rem(32);
  grid-row-gap: rem(16);
  grid-template-columns: 1fr;
}

.locationFormHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.deleteButton,
.addLocation {
  border: none;
  background: transparent;
  color: $black-clr;
}

.addLocation {
  display: flex;
  align-items: center;

  svg {
    margin-right: rem(8);
    color: $functional-clr-info;
  }
  span {
    position: relative;
    font-family: $font-family-heavy;
    font-size: rem($font-size-small);
    line-height: rem($font-line-height-xtra-small);

    @include media-breakpoint-up(lg) {
      font-size: rem($font-size);
      line-height: rem($font-line-height-small);
    }

    &::after {
      position: absolute;
      content: '';
      bottom: rem(-1);
      left: 0;
      width: 100%;
      height: rem(1);
      background-color: $black-clr;
    }
  }
}
</style>
